package fr.manyverse.app.presentation.components.modules.timer

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Pause
import androidx.compose.material.icons.filled.PlayArrow
import androidx.compose.material.icons.filled.Stop
import androidx.compose.material.icons.filled.Timer
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableIntStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import fr.manyverse.app.domain.model.ActiveTimer
import kotlinx.coroutines.delay
import manyverse.shared.generated.resources.Res
import manyverse.shared.generated.resources.timer_expired
import manyverse.shared.generated.resources.timer_pause
import manyverse.shared.generated.resources.timer_resume
import manyverse.shared.generated.resources.timer_stop
import manyverse.shared.generated.resources.timer_targeted_players
import org.jetbrains.compose.resources.stringResource
import kotlin.time.Clock

@Composable
internal fun TimerGmCard(
    timer: ActiveTimer,
    onStop: () -> Unit,
    onTogglePause: () -> Unit,
    onExpired: () -> Unit,
    modifier: Modifier = Modifier
) {
    // Keyed on the whole timer so that a pause/resume recomputes the countdown
    var remainingSeconds by remember(timer) { mutableIntStateOf(timer.remainingSecondsAt(Clock.System.now())) }

    // Tick every second, frozen while paused
    LaunchedEffect(timer) {
        if (timer.isPaused && remainingSeconds > 0) return@LaunchedEffect
        while (remainingSeconds > 0) {
            delay(1000)
            remainingSeconds = timer.remainingSecondsAt(Clock.System.now())
        }
        onExpired()
    }

    val isUrgent = remainingSeconds <= (timer.durationSeconds * 0.1).toInt().coerceAtLeast(1)
    val isExpired = remainingSeconds <= 0

    Card(
        modifier = modifier.fillMaxWidth(),
        colors = CardDefaults.cardColors(
            containerColor = MaterialTheme.colorScheme.surfaceVariant
        )
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(12.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(12.dp)
        ) {
            Icon(
                imageVector = Icons.Default.Timer,
                contentDescription = null,
                tint = if (isUrgent) MaterialTheme.colorScheme.error else MaterialTheme.colorScheme.primary,
                modifier = Modifier.size(24.dp)
            )

            Column(modifier = Modifier.weight(1f)) {
                Text(
                    text = if (isExpired) {
                        stringResource(Res.string.timer_expired)
                    } else {
                        formatCountdown(remainingSeconds)
                    },
                    style = MaterialTheme.typography.titleMedium,
                    color = if (isUrgent) MaterialTheme.colorScheme.error else MaterialTheme.colorScheme.onSurface
                )
                Text(
                    text = stringResource(
                        Res.string.timer_targeted_players,
                        timer.targetParticipants.size
                    ),
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant
                )
            }

            if (!isExpired) {
                IconButton(onClick = onTogglePause) {
                    Icon(
                        imageVector = if (timer.isPaused) Icons.Default.PlayArrow else Icons.Default.Pause,
                        contentDescription = stringResource(if (timer.isPaused) Res.string.timer_resume else Res.string.timer_pause),
                        tint = MaterialTheme.colorScheme.primary
                    )
                }
                IconButton(onClick = onStop) {
                    Icon(
                        imageVector = Icons.Default.Stop,
                        contentDescription = stringResource(Res.string.timer_stop),
                        tint = MaterialTheme.colorScheme.error
                    )
                }
            }
        }
    }
}

